<script setup lang="ts">
import type { Property } from '~/core/entities/property';

import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

import { PropertyPhotoCarouselDesktop } from '~/components/PropertyPhotoCarouselDesktop';
import { PropertyPhotoCarouselMobile } from '~/components/PropertyPhotoCarouselMobile';

// Note: These carousels are critical for LCP and use CSS-based visibility (hidden/xl:block, xl:hidden)
// Lazy hydration with media queries causes hydration mismatches because both are SSR-rendered
// but the wrapper elements interfere with DOM matching during hydration

interface PropertyHeroCarouselsProps {
	property: Property | null;
	isLoading: boolean;
	shouldShowFeaturedVideo: boolean;
}

interface PropertyHeroCarouselsEmits {
	'photo-clicked': [
		payload: {
			photoIndex: number;
			photoUrl: string;
			clickType: 'photo' | 'cta';
		}
	];
	'video-clicked': [videoUrl?: string];
}

const props = defineProps<PropertyHeroCarouselsProps>();
defineEmits<PropertyHeroCarouselsEmits>();

function normalizeVideoDimension(value: unknown): number | undefined {
	const normalizedValue = Number(value);

	return Number.isFinite(normalizedValue) && normalizedValue > 0
		? normalizedValue
		: undefined;
}

const featuredVideo = computed(() =>
	props.shouldShowFeaturedVideo
		? props.property?.featuredVideos?.[0]
		: undefined
);

const featuredVideoUrl = computed(() => featuredVideo.value?.url);
const featuredVideoPosterUrl = computed(() => featuredVideo.value?.thumbnail);

// Keep the carousel structure identical between SSR and the first client render
// by passing the same video metadata to both CSS-controlled layouts. The
// breakpoint only controls the ClientOnly player mounted inside each carousel,
// so a single Shaka instance loads without replacing hydrated DOM.
const desktopBreakpoint = import.meta.client
	? useBreakpoints(breakpointsTailwind).greaterOrEqual('xl')
	: null;
const isDesktop = computed(() => desktopBreakpoint?.value ?? false);
const featuredVideoWidth = computed(() =>
	normalizeVideoDimension(featuredVideo.value?.width)
);
const featuredVideoHeight = computed(() =>
	normalizeVideoDimension(featuredVideo.value?.height)
);
</script>

<template>
	<!-- Photo Carousels - both rendered with CSS-based visibility for LCP optimization -->
	<PropertyPhotoCarouselDesktop
		:property="property"
		:images="property?.images || []"
		:is-loading="isLoading"
		:video-url="featuredVideoUrl"
		:poster-url="featuredVideoPosterUrl"
		:video-width="featuredVideoWidth"
		:video-height="featuredVideoHeight"
		:should-load-video="isDesktop"
		section-name="hero section"
		@photo-clicked="$emit('photo-clicked', $event)"
		@video-clicked="$emit('video-clicked', $event)"
	/>

	<PropertyPhotoCarouselMobile
		:property="property"
		:images="property?.images || []"
		:is-loading="isLoading"
		:video-url="featuredVideoUrl"
		:poster-url="featuredVideoPosterUrl"
		:video-width="featuredVideoWidth"
		:video-height="featuredVideoHeight"
		:should-load-video="!isDesktop"
		section-name="hero section"
		@photo-clicked="$emit('photo-clicked', $event)"
		@video-clicked="$emit('video-clicked', $event)"
	/>
</template>
